import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePerformanceCycle } from '../contexts/PerformanceCycleContext';
import { cn } from '../lib/utils';
import {
  LayoutDashboard,
  Target,
  Users,
  Award,
  Settings,
  LogOut,
  Building2,
  ClipboardCheck,
  UserCheck,
  Download,
  Shield,
  Briefcase,
  Calendar,
  Lock,
  BarChart3,
  BookOpen,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useState, useEffect } from 'react';
import { settingsApi } from '../lib/api';
import { toast } from 'sonner';
import packageJson from '../../package.json';

const NavItem = ({ to, icon: Icon, children, end = false, disabled = false, disabledMessage = '' }) => {
  if (disabled) {
    return (
      <div
        className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-sidebar-foreground/40 cursor-not-allowed"
        onClick={() => {
          if (disabledMessage) {
            toast.error(disabledMessage);
          }
        }}
        title={disabledMessage}
      >
        <Icon className="h-4 w-4" />
        {children}
        <Lock className="h-3 w-3 ml-auto" />
      </div>
    );
  }
  
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
          isActive
            ? 'bg-sidebar-accent text-sidebar-accent-foreground'
            : 'text-sidebar-foreground/80 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground'
        )
      }
    >
      <Icon className="h-4 w-4" />
      {children}
    </NavLink>
  );
};

export const Sidebar = () => {
  const { user, logout, isAdmin, isHR, isManager } = useAuth();
  const { isStageActive, formatStageDate, getStageStartDate } = usePerformanceCycle();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await settingsApi.get();
        setSettings(response.data);
      } catch (error) {
        console.error('Failed to load settings:', error);
      }
    };
    loadSettings();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex flex-col h-full w-64 bg-secondary text-secondary-foreground">
      {/* Logo */}
      <div className="p-4 border-b border-sidebar-border">
        <div className="flex items-center gap-3">
          {settings?.logo_url ? (
            <img src={settings.logo_url} alt="Logo" className="h-10 w-10 object-contain rounded" />
          ) : (
            <div className="h-10 w-10 bg-primary rounded-lg flex items-center justify-center">
              <Building2 className="h-6 w-6 text-primary-foreground" />
            </div>
          )}
          <div>
            <h1 className="font-bold text-lg leading-tight">{settings?.company_name || 'Performance Portal'}</h1>
            <p className="text-xs text-sidebar-foreground/60">Performance Portal</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        <NavItem to="/dashboard" icon={LayoutDashboard} end>
          Dashboard
        </NavItem>

        <NavItem to="/goals" icon={Target}>
          My Goals
        </NavItem>

        {(isManager || isAdmin || isHR) && (
          <NavItem to="/team-goals" icon={ClipboardCheck}>
            Team Goals
          </NavItem>
        )}

        {(isAdmin || isHR) && (
          <NavItem 
            to="/reviews/calibration" 
            icon={UserCheck}
            disabled={!isStageActive('calibration')}
            disabledMessage={`Calibration starts ${formatStageDate(getStageStartDate('calibration'))}`}
          >
            Calibration
          </NavItem>
        )}

        {(isAdmin || isHR) && (
          <NavItem to="/organization-status" icon={BarChart3}>
            Organization Status
          </NavItem>
        )}

        {(isAdmin || isHR) && (
          <>
            <div className="pt-4 pb-2">
              <p className="px-3 text-xs font-semibold text-sidebar-foreground/50 uppercase tracking-wider">
                Administration
              </p>
            </div>
            <NavItem to="/performance-cycle" icon={Calendar}>
              Performance Cycle
            </NavItem>
            <NavItem to="/users" icon={Users}>
              User Management
            </NavItem>
            <NavItem to="/competencies" icon={Award}>
              Competencies
            </NavItem>
            {isAdmin && (
              <NavItem to="/goal-library" icon={BookOpen}>
                Goal Library
              </NavItem>
            )}
            <NavItem to="/reports" icon={Download}>
              Reports & Export
            </NavItem>
            <NavItem to="/audit-logs" icon={Shield}>
              Audit Logs
            </NavItem>
            {isAdmin && (
              <NavItem to="/org-settings" icon={Briefcase}>
                Org Settings
              </NavItem>
            )}
          </>
        )}

        {isAdmin && (
          <NavItem to="/settings" icon={Settings}>
            Settings
          </NavItem>
        )}
      </nav>

      {/* User Profile */}
      <div className="p-4 border-t border-sidebar-border">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-10 w-10 rounded-full bg-sidebar-accent flex items-center justify-center">
            <span className="text-sm font-medium">
              {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            {user?.role === 'admin' && (
              <p className="text-sm font-medium truncate">{user?.name}</p>
            )}
            <p className="text-xs text-sidebar-foreground/60 capitalize">{user?.role}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          className="w-full justify-start text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Sign Out
        </Button>
        {user?.role === 'admin' && (
          <p className="text-[10px] text-sidebar-foreground/40 text-center mt-2">
            v{packageJson.version}
          </p>
        )}
      </div>
    </div>
  );
};
